import { action } from '@ember/object';
import { next } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { didCancel, task } from 'ember-concurrency';
import LazyLoad, { type ILazyLoadInstance } from 'vanilla-lazyload';

import type StoreService from '@ember-data/store';
import type HotelModel from 'admin-panel/models/hotel';
import type PdfModel from 'admin-panel/models/pdf';
import type SessionInfoService from 'admin-panel/services/session-info';

interface UiPdfPickerModalSignature {
  Args: {
    Named: {
      isOpen: boolean;
      onClose: () => void;
      onSelect: (pdf: PdfModel) => void;
    };
  };
}

function compareIds(leftId: string, rightId: string) {
  return leftId.localeCompare(rightId, undefined, { numeric: true });
}

interface PdfPickerData {
  pdfs: PdfModel[];
  installations: { id: string; name: string }[];
}

export default class UiPdfPickerModalComponent extends Component<UiPdfPickerModalSignature> {
  @service
  declare store: StoreService;

  @service
  declare sessionInfo: SessionInfoService;

  private lazyLoad: ILazyLoadInstance | null = null;
  private scrollContainerElement: HTMLElement | null = null;
  private cachedHotelId: string | null = null;

  @tracked
  pdfs: PdfModel[] = [];

  @tracked
  installations: { id: string; name: string }[] = [];

  @tracked
  selectedInstallationIds: string[] = [];

  @tracked
  searchValue = '';

  @tracked
  hasLoadError = false;

  get hotel(): HotelModel | null {
    const { hotel } = this.sessionInfo;

    return hotel ?? null;
  }

  get isUnavailable() {
    return !this.hotel;
  }

  get hotelId() {
    return this.hotel?.id;
  }

  get filteredPdfs() {
    const installationIds = this.selectedInstallationIds;
    const normalizedSearchValue = this.searchValue.toLocaleLowerCase();

    return this.pdfs.filter((pdf) => {
      const matchesInstallation =
        installationIds.length === 0 ||
        installationIds.includes(pdf.belongsTo('hotel').id());
      const matchesSearch = pdf.name
        .toLocaleLowerCase()
        .includes(normalizedSearchValue);

      return matchesInstallation && matchesSearch;
    });
  }

  get hasActiveFilters() {
    return (
      this.selectedInstallationIds.length > 0 || this.searchValue.length > 0
    );
  }

  willDestroy() {
    super.willDestroy();

    this.setupModalTask.cancelAll();
    this.teardownLazyLoad();
  }

  setupModalTask = task({ restartable: true }, async () => {
    this.hasLoadError = false;

    const { hotel } = this;
    const hotelId = hotel?.id ?? null;

    if (hotelId !== this.cachedHotelId) {
      this.resetPickerState();
      this.cachedHotelId = hotelId;
    }

    if (!hotel) {
      this.destroyLazyLoad();

      return;
    }

    try {
      const data = await this.fetchPdfs(hotel);

      if (!this.isCurrentHotel(hotel.id)) {
        return;
      }

      this.publishPickerData(data);
    } catch (error) {
      if (didCancel(error)) return;

      this.handleLoadError();

      return;
    }

    this.initializeLazyLoad();
  });

  private async fetchPdfs(hotel: HotelModel): Promise<PdfPickerData> {
    const hotelMixinIds = hotel.hasMany('hotelMixin').ids();
    const installationIds = [hotel.id, ...hotelMixinIds];
    const installationRanks = new Map(
      installationIds.map((installationId, index) => [installationId, index])
    );
    const pdfs = (
      await this.store.query('pdf', {
        'filter[hotel_id_in]': installationIds,
        'filter[s]': 'id',
      })
    )
      .toArray()
      .filter((pdf) => pdf.isInsertable);

    const sortedPdfs = pdfs.sort((leftPdf, rightPdf) => {
      const leftHotelId = leftPdf.belongsTo('hotel').id();
      const rightHotelId = rightPdf.belongsTo('hotel').id();
      const installationComparison =
        (installationRanks.get(leftHotelId) ?? Number.MAX_SAFE_INTEGER) -
        (installationRanks.get(rightHotelId) ?? Number.MAX_SAFE_INTEGER);

      return installationComparison || compareIds(leftPdf.id, rightPdf.id);
    });
    return {
      pdfs: sortedPdfs,
      installations: [
        { id: hotel.id, name: hotel.name },
        ...hotelMixinIds.map((mixinId) => ({
          id: mixinId,
          name:
            hotel.sharedInstallationMetadata.find(
              (mixin) => mixinId in mixin
            )?.[mixinId] ?? mixinId,
        })),
      ],
    };
  }

  private isCurrentHotel(hotelId: string) {
    return this.args.isOpen && this.hotel?.id === hotelId;
  }

  private publishPickerData(data: PdfPickerData) {
    this.pdfs = data.pdfs;
    this.installations = data.installations;
  }

  private resetPickerState() {
    this.pdfs = [];
    this.installations = [];
    this.selectedInstallationIds = [];
    this.searchValue = '';
  }

  private handleLoadError() {
    this.resetPickerState();
    this.hasLoadError = true;
    this.destroyLazyLoad();
  }

  @action
  registerScrollContainer(element: Element) {
    if (!(element instanceof HTMLElement)) {
      return;
    }

    this.scrollContainerElement = element;
    this.initializeLazyLoad();
  }

  @action
  handleOpenStateChange() {
    if (!this.args.isOpen) {
      this.setupModalTask.cancelAll();
      this.teardownLazyLoad();
    }
  }

  @action
  handleHotelChange() {
    if (this.args.isOpen) {
      void this.setupModalTask.perform();
    }
  }

  @action
  toggleInstallationFilter(installation: { id: string; name: string }) {
    if (this.selectedInstallationIds.includes(installation.id)) {
      this.selectedInstallationIds = this.selectedInstallationIds.filter(
        (id) => id !== installation.id
      );
    } else {
      this.selectedInstallationIds = [
        ...this.selectedInstallationIds,
        installation.id,
      ];
    }

    this.updateLazyLoad();
  }

  @action
  updateSearchValue(value: string) {
    this.searchValue = value;
    this.updateLazyLoad();
  }

  @action
  handlePdfClick(pdf: PdfModel) {
    // Loaded records are insertable-only; keep this guard for direct callers.
    if (this.setupModalTask.isRunning || !pdf.isInsertable) {
      return;
    }

    this.args.onSelect(pdf);
  }

  private initializeLazyLoad() {
    if (
      !this.args.isOpen ||
      this.isUnavailable ||
      !this.scrollContainerElement
    ) {
      this.destroyLazyLoad();

      return;
    }

    this.lazyLoad?.destroy();
    this.lazyLoad = new LazyLoad({
      use_native: false,
      cancel_on_exit: true,
      container: this.scrollContainerElement,
      elements_selector: '[lazy-load-pdf-preview]',
    });

    this.updateLazyLoad();
  }

  private updateLazyLoad() {
    next(() => {
      this.lazyLoad?.update();
    });
  }

  private destroyLazyLoad() {
    this.lazyLoad?.destroy();
    this.lazyLoad = null;
  }

  private teardownLazyLoad() {
    this.destroyLazyLoad();
    this.scrollContainerElement = null;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Ui::PdfPickerModal': typeof UiPdfPickerModalComponent;
    'ui/pdf-picker-modal': typeof UiPdfPickerModalComponent;
  }
}
